import React, { useEffect, useState } from "react";
import { getLinkClickAttrs, usePageUrl } from "src/utils/link-click";
import { linkNameOnPress } from "src/utils/press-link-name";

export interface YotpoPackageRatingProps {
  enableStarRating ?: boolean;
  starColor?: string;
  enableYotpoReview?: boolean;
  appKey?: string;
  analyticsLinkName?: string;
  productId?: string;
  scriptSrc?: string;
  yotpoAttributes?: {
    className?: string;
    data_yotpo_instance_id?: string;
    data_yotpo_product_id?: string;
    [key: string]: any; // For additional field if any
  }
}

export const YotpoPackageRating = (props: YotpoPackageRatingProps) => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  // WAT-12478 — GTM auto-capture reports the star-rating click from these attributes.
  const pageUrl = usePageUrl();
  const appKey =  props.appKey || "W8MDa4JClXLdME9zf4AJG79SnaMwQsqU1vCRLyLh";
  const initializeYotpo = () => {
    if (window?.yotpo) {
      console.log('Initializing Yotpo widgets');
      setTimeout(() => {
        if (window.yotpo.initWidgets) {
          window.yotpo.initWidgets();
        } else {
          console.log('Yotpo initWidgets method is not available');
        }
      }, 500);
    } else {
      console.log('Yotpo object is not available on the window');
    }
  };
  useEffect(() => {
    if (typeof document !== "undefined" && props?.enableStarRating) {
      const existingScript = document.querySelector(`script[src="${props?.scriptSrc}"]`);
      if (!existingScript) {
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.src = `${props?.scriptSrc}`;
        script.onload = () => {
          setScriptLoaded(true);
          initializeYotpo();
        };
        script.onerror = () => {
          setScriptLoaded(false);
        };
        document.body.appendChild(script);
      } else {
        setScriptLoaded(true);
      }
    }
  }, [appKey]);
  useEffect(() => {
    if (scriptLoaded) {
      const yotpoWidgetContainer = document.querySelector('.yotpo.bottomLine');
      if (yotpoWidgetContainer) {
        initializeYotpo();
      }
    }
  }, [scriptLoaded]);
  useEffect(() => {
    const applyStyles = () => {
      const yotpoBottomLineDiv = document.querySelector('.yotpo.bottomLine');
      if (yotpoBottomLineDiv) {
        const spans = yotpoBottomLineDiv.querySelectorAll(
          '.yotpo-icon.yotpo-icon-star.rating-star.pull-left, .yotpo-icon.yotpo-icon-half-star.rating-star.pull-left'
        );
        spans.forEach(span => {
          (span as HTMLElement).style.color = props?.starColor || '#e7721b';
        });
        const textElements = yotpoBottomLineDiv.querySelectorAll('.yotpo .text-m');
        textElements.forEach(textElement => {
          (textElement as HTMLElement).style.fontSize = '16px';
          (textElement as HTMLElement).style.marginLeft = '16px';
        });
      }
    }
    applyStyles();

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.addedNodes.length > 0) {
          applyStyles();
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    return () => {
      observer.disconnect();
    };
  }, []);

  const transformKeys = (obj: { [key: string]: any }) => {
    const newObj: { [key: string]: any } = {};
    // eslint-disable-next-line guard-for-in
    for (const key in obj) {
      const newKey = key.replace(/_/g, '-');
      newObj[newKey] = obj[key];
    }
    return newObj;
  };
  const transformedyotpoConfig = transformKeys(props?.yotpoAttributes);
  const ratingLinkAttrs = getLinkClickAttrs(props?.analyticsLinkName, 'body', pageUrl);
  // The container is a role-less div, so this code gives it no static aria-label (a CMS-authored
  // one in yotpoAttributes is still spread, as before): only the data-*, which make GTM report a
  // click anywhere inside the widget from this element. The CMS link name (unchanged vocabulary)
  // is written as its aria-label for the press only and the previous value put back right after
  // the click (src/utils/press-link-name) — the stars / review link keep their own names.
  const ratingPress = linkNameOnPress(ratingLinkAttrs);
  const getYotpoWidgetStarRating = () => (
       <>
         <div className={transformedyotpoConfig.className} {...transformedyotpoConfig}
           onPointerDownCapture={ratingPress.onPointerDownCapture}
           onKeyDownCapture={ratingPress.onKeyDownCapture}
           data-linkposition={ratingLinkAttrs?.['data-linkposition'] ?? transformedyotpoConfig?.['data-linkposition']}
           data-linkdestination={ratingLinkAttrs?.['data-linkdestination'] ?? transformedyotpoConfig?.['data-linkdestination']}></div>
       </>
  )
  return (
    <>
      {props?.enableStarRating  && getYotpoWidgetStarRating()}
    </>
  )
}

export default YotpoPackageRating;
